import React, { useState, useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual'; 
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Button,
  makeStyles
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  currencyFormat
} from '../../../services/common'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    border: '1px solid #ccc',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    width: '100%',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  laborRow: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
  },
  totals: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(2),
  },
  totalText: {
    fontWeight: 500,
    fontSize: '1rem',
  }
}));

const laborTypes = [
  { value: 'labor', display: 'General Labor', defaultRate: 60, defaultSize: 3 },
  { value: 'install', display: 'Install', defaultRate: 70, defaultSize: 4 },
  { value: 'supervisor', display: 'Supervisor', defaultRate: 95, defaultSize: 1 },
  { value: 'sprayTech', display: 'Spray Tech', defaultRate: 90, defaultSize: 1 },
  { value: 'tech', display: 'Tech', defaultRate: 95, defaultSize: 1 },
  { value: 'operator', display: 'Operator', defaultRate: 90, defaultSize: 1 },
  { value: 'treeWorker', display: 'Tree Worker', defaultRate: 85, defaultSize: 2 },
  { value: 'arborist', display: 'Arborist', defaultRate: 95, defaultSize: 1 },
  { value: 'irrigationTech', display: 'Irrigation Tech', defaultRate: 150, defaultSize: 1 },
  { value: 'lightingTech', display: 'Lighting Tech', defaultRate: 95, defaultSize: 2 },
  { value: 'bobcatOperator', display: 'Bobcat Operator', defaultRate: 300, defaultSize: 1 },
  { value: 'tractorOperator', display: 'Tractor Operator', defaultRate: 300, defaultSize: 1 },
  { value: 'craneOperator', display: 'Crane Operator', defaultRate: 900, defaultSize: 1 }
];

const PerCrewPerDayConfiguration = (props) => {
  const { showRates = true, initialValues = [], frequency, client, onChange } = props;
  const classes = useStyles();
  const [laborItems, setLaborItems] = useState(initialValues.length > 0 ? initialValues : [createDefaultLaborItem()]);
  const [totals, setTotals] = useState({ totalCost: 0, totalHours: 0 });

  const prevValuesRef = useRef();

  useEffect(() => {
    const currentValues = { initialValues, frequency, client };

    if (!isEqual(prevValuesRef.current, currentValues)) {
      prevValuesRef.current = currentValues;
      calculateTotals();
    }
  }, [initialValues, frequency, client]);


  function createDefaultLaborItem() {
    return {
      id: Date.now().toString(), // Unique ID for each item
      laborType: '',
      numberOfMen: '',
      hoursPerVisit: '',
      ratePerHour: ''
    };
  }

  // Calculate totals whenever labor items change
  useEffect(() => {
    calculateTotals();
    // Notify parent of changes
    if (onChange) {
      onChange(laborItems);
    }
  }, [laborItems]);

  const calculateTotals = () => {
    const newTotals = laborItems.reduce((acc, item) => {
      const hours = Number(item.numberOfMen || 0) * Number(item.hoursPerVisit || 0);
      const cost = hours * Number(item.ratePerHour || 0);
     
      return {
        totalHours: acc.totalHours + hours,
        totalCost: acc.totalCost + cost
      };
    }, { totalHours: 0, totalCost: 0 });
    
    // setTotals(newTotals);
    const monthlyTotal = calculateMonthlyValue(newTotals, frequency)
    // console.log('Monthly Total...', monthlyTotal)
    setTotals({ ...newTotals, monthlyValue: monthlyTotal });

  };

  const handleFocus = (event) => event.target.select();

  const handleChange = (index, field, value) => {
    const updatedItems = [...laborItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // If labor type changes, update default values
    if (field === 'laborType') {
      const laborType = laborTypes.find(type => type.value === value);
      if (laborType) {
        updatedItems[index].numberOfMen = laborType.defaultSize;
        
        // Set rate based on client rates or default
        let rate = laborType.defaultRate;
        
        // Check if client has labor rates and if the specific labor type rate exists
        if (client && client.laborRates && client.laborRates[value] && client.laborRates[value].rate) {
          rate = client.laborRates[value].rate;
        }
        
        updatedItems[index].ratePerHour = rate;
      }
    }

    setLaborItems(updatedItems);
  };

  const addLaborItem = () => {
    setLaborItems([...laborItems, createDefaultLaborItem()]);
  };

  const removeLaborItem = (index) => {
    if (laborItems.length > 1) {
      const updatedItems = [...laborItems];
      updatedItems.splice(index, 1);
      setLaborItems(updatedItems);
    }
  };

  return (
    <Paper elevation={1} className={classes.container}>
      <Typography variant="h6" gutterBottom>
        Labor Details Configuration
      </Typography>
      
      {laborItems.map((item, index) => (
        <Grid container spacing={2} key={item.id} className={classes.laborRow}>
          <Grid item xs={12} sm={4}>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
              <InputLabel>Labor Type</InputLabel>
              <Select
                value={item.laborType}
                onChange={(e) => handleChange(index, 'laborType', e.target.value)}
                label="Labor Type"
              >
                <MenuItem value="">Select Type</MenuItem>
                {laborTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.display}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Workers"
              variant="outlined"
              size="small"
              type="number"
              value={item.numberOfMen}
              onChange={(e) => handleChange(index, 'numberOfMen', e.target.value)}
              onFocus={handleFocus}
              fullWidth
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Hours Per Visit"
              variant="outlined"
              size="small"
              type="number"
              value={item.hoursPerVisit}
              onChange={(e) => handleChange(index, 'hoursPerVisit', e.target.value)}
              onFocus={handleFocus}
              fullWidth
              InputProps={{ inputProps: { min: 0, step: 0.5 } }}
            />
          </Grid>

          {showRates && (
            <Grid item xs={12} sm={2}>
              <TextField
                label="Rate/Hr"
                variant="outlined"
                size="small"
                type="number"
                value={item.ratePerHour}
                onChange={(e) => handleChange(index, 'ratePerHour', e.target.value)}
                onFocus={handleFocus}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={2} container alignItems="center" justifyContent="center">
            <IconButton 
              color="secondary" 
              onClick={() => removeLaborItem(index)}
              disabled={laborItems.length === 1}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={addLaborItem}
        className={classes.addButton}
      >
        Add Labor Type
      </Button>

      <div className={classes.totals}>
        <Typography className={classes.totalText}>
          Total Man Hours: {totals.totalHours.toFixed(1)}
        </Typography>
        {showRates && (
          <Typography className={classes.totalText}>
            Total Per Visit: {currencyFormat(totals.totalCost)}
          </Typography>
        )}
        <Typography className={classes.totalText}>
          Est. Monthly: {currencyFormat(totals.monthlyValue)}
        </Typography>

      </div>
    </Paper>
  );
};

function calculateMonthlyValue(totals, origFrequency) {
  // console.log('origFrequency', origFrequency)
  const frequencyMap = {
    'daily': 260,
    'fourdaysperweek': 208,
    'everyotherday(m-w-f)': 156,
    'twiceaweek': 104,
    'weekly': 52,
    'bi-weekly': 26,
    'monthly': 12,
    'quarterly': 4,
    'semi-annually': 2,
    'annually': 1,
    'ondemand': 0
  };

  const frequencyKey = origFrequency?.toLowerCase().replace(/\s+/g, '');
  const visitsPerYear = frequencyMap[frequencyKey];
  // console.log('Frequency Key', frequencyKey)
  if (visitsPerYear === undefined) {
    console.warn(`Unknown frequency: ${origFrequency}`);
    return 0;
  }

  // If frequency is 0 (on-demand), then there's no monthly value
  if (visitsPerYear === 0) return 0;

  const annualCost = totals.totalCost * visitsPerYear;
  const monthlyValue = annualCost / 12;

  return monthlyValue;
}

export default PerCrewPerDayConfiguration;
